import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlmPartner,
  EnumPlmPartnerPartnerType,
  useRemovePlmPartnerMutation,
  usePlmPartnersQuery,
  usePlmPartnerListSubSubscription,
} from '../../../../types/generated-types';
import {
  MaterialReactTable,
  MRT_VisibilityState,
  MRT_ColumnDef,
} from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { BottomMenuItem } from '../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import {
  useIsSmallScreen,
  useIsMediumScreen,
} from '../../../system/SystemMediaQueriesProvider';

type MyPlmPartner = Partial<PlmPartner>;

function formatPlmPartnerTypeLabel(
  value: EnumPlmPartnerPartnerType | string | null | undefined,
): string {
  if (value === EnumPlmPartnerPartnerType.Utility || value === 'utility') {
    return 'Utility';
  }
  if (
    value === EnumPlmPartnerPartnerType.Aggregator ||
    value === 'aggregator'
  ) {
    return 'Aggregator';
  }
  return value ? String(value) : '—';
}

export const PlmPartnerList = () => {
  const navigate = useNavigate();
  const isSmall = useIsSmallScreen();
  const isMedium = useIsMediumScreen();
  const { can } = useAuthorizer();
  const [plmPartners, setPlmPartners] = useState<MyPlmPartner[]>([]);

  const {
    data: myPlmPartners,
    loading,
    error,
  } = usePlmPartnersQuery({
    fetchPolicy: 'network-only',
  });

  const [deletePlmPartner] = useRemovePlmPartnerMutation({
    refetchQueries: ['PlmPartners'],
  });

  useEffect(() => {
    setPlmPartners(myPlmPartners?.plmPartners ? myPlmPartners.plmPartners : []);
  }, [myPlmPartners]);

  usePlmPartnerListSubSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the PLM partners: ', error);
    }
  }, [error]);

  const handleRemovePlmPartner = useCallback(
    async (id: string) => {
      if (!can('delete', 'PlmPartner')) {
        Notifier.error('You do not have permission to delete PLM partners');
        return;
      }

      try {
        await deletePlmPartner({
          variables: {
            id,
          },
          refetchQueries: ['PlmPartners'],
        });

        Notifier.success('PLM partner removed');
      } catch (e) {
        Notifier.error('Error removing PLM partner');
        console.error(e);
      }
    },
    [deletePlmPartner, can],
  );

  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();

  useEffect(() => {
    const items: BottomMenuItem[] = [];

    if (can('create', 'PlmPartner')) {
      items.push({
        id: 'add-plmPartner-menu-item',
        label: isSmall ? 'Add PLM Partner' : 'Add Peak Load Management Partner',
        icon: <Add fontSize="small" />,
        navTarget: '/plmPartners/add-plmPartner',
      });
    }

    setContextMenuItems(items.length > 0 ? [{ items }] : undefined);

    return () => {
      setContextMenuItems(undefined);
    };
  }, [setContextMenuItems, can, isSmall]);

  React.useEffect(() => {
    setTitle(isSmall ? 'PLM Partners' : 'Peak Load Management Partners');
    setTitlePath('/plmPartners');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {
      description: !(isSmall || isMedium),
    },
  );

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      description: !(isSmall || isMedium),
    }));
  }, [isSmall, isMedium]);

  const columns = React.useMemo<MRT_ColumnDef<MyPlmPartner>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'partnerType',
        header: 'Partner type',
        Cell: ({ cell }) =>
          formatPlmPartnerTypeLabel(
            cell.getValue<EnumPlmPartnerPartnerType | string>(),
          ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell(props) {
          const description = props.cell.getValue<string>();
          return (
            <Box
              title={description}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minWidth: '175px',
              }}
            >
              {description}
            </Box>
          );
        },
      },
      {
        accessorKey: 'contact.name',
        header: 'Contact Name',
      },
      {
        accessorKey: 'contact.email',
        header: 'Contact Email',
        Cell: ({ cell }) => {
          const email = cell.getValue<string>();
          return (
            <a
              href={`mailto:${email}`}
              style={{
                textDecoration: 'none',
                color: '#36AFA5',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </a>
          );
        },
      },
      {
        accessorKey: 'contact.phone',
        header: 'Contact Phone',
        Cell: ({ cell }) => {
          const phone = cell.getValue<string>();
          return (
            <a
              href={`tel:${phone}`}
              style={{ textDecoration: 'none', color: '#36AFA5' }}
            >
              {phone}
            </a>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 160,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: '0px', maxWidth: 150 }}>
            <Tooltip title="View">
              <IconButton
                onClick={() =>
                  navigate(`/plmPartners/${row.original._id}/view-plmPartner`)
                }
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            {can('update', 'PlmPartner') && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() =>
                    navigate(`/plmPartners/${row.original._id}/edit-plmPartner`)
                  }
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {can('delete', 'PlmPartner') && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() =>
                    row.original._id && handleRemovePlmPartner(row.original._id)
                  }
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [navigate, handleRemovePlmPartner, can],
  );

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={columns}
        data={plmPartners}
        defaultColumn={{ size: 125 }}
        state={{
          isLoading: loading,
          columnVisibility,
        }}
        onColumnVisibilityChange={setColumnVisibility}
        enablePagination={plmPartners?.length > 10}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        muiTableBodyRowProps={{ hover: false }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        layoutMode="semantic"
        initialState={{
          columnVisibility: {
            description: !(isSmall || isMedium),
          } as MRT_VisibilityState,
        }}
      />
    </Box>
  );
};
